import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    /** 제목 아래 보조 문구(UX·모바일 힌트 등) */
    description?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: 'small' | 'medium' | 'large' | 'xl' | 'full' | string;
    maxWidth?: string;
    /** 다이얼로그 루트 컨테이너에 추가 클래스 (예: `h-[min(36rem,85vh)]` 로 높이 고정) */
    dialogClassName?: string;
}

export default function Modal({ isOpen, onClose, title, description, children, footer, size = 'medium', maxWidth, dialogClassName }: ModalProps) {
    /** 백드롭에서 눌렀다 뗄 때만 닫기 — 모달 안에서 드래그해 밖에서 놓아도 닫히지 않음 */
    const closeGestureStartedOnBackdropRef = useRef(false);

    // Close on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const sizeClasses = {
        small: 'max-w-md',
        medium: 'max-w-lg',
        large: 'max-w-2xl',
        xl: 'max-w-4xl',
        full: 'max-w-full mx-4'
    };

    const currentSizeClass = maxWidth || sizeClasses[size as keyof typeof sizeClasses] || size;

    const handleBackdropPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        closeGestureStartedOnBackdropRef.current = e.target === e.currentTarget;
    };

    const handleBackdropPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (
            closeGestureStartedOnBackdropRef.current &&
            e.target === e.currentTarget
        ) {
            onClose();
        }
        closeGestureStartedOnBackdropRef.current = false;
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 transition-opacity"
            onPointerDown={handleBackdropPointerDown}
            onPointerUp={handleBackdropPointerUp}
            onPointerCancel={() => {
                closeGestureStartedOnBackdropRef.current = false;
            }}
        >
            <div
                className={`bg-white rounded-xl shadow-2xl w-full flex flex-col max-h-[90vh] ${currentSizeClass} animate-in fade-in zoom-in-95 duration-200 ${dialogClassName ?? ''}`.trim()}
                onPointerDown={(e: React.PointerEvent) => {
                    closeGestureStartedOnBackdropRef.current = false;
                    e.stopPropagation();
                }}
            >
                <div className="flex items-start justify-between gap-3 px-6 py-3 border-b border-gray-100 shrink-0">
                    <div className="min-w-0 flex-1 pr-2">
                        <h2 className="text-lg font-bold text-gray-900 leading-tight">{title}</h2>
                        {description != null && description !== '' && (
                            <p className="text-sm text-gray-500 font-normal mt-1.5 leading-relaxed">{description}</p>
                        )}
                    </div>
                    <button
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                        onClick={onClose}
                    >
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto flex-1 min-h-0">
                    {children}
                </div>
                {footer && (
                    <div className="flex justify-end items-center gap-2 px-6 py-3 bg-gray-50 border-t border-gray-100 rounded-b-xl shrink-0">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}
